/**
 * ============================================================================
 * Admin Companies Page
 * ============================================================================
 * 
 * Manage companies (CRUD operations).
 * 
 * Features:
 * - List all companies
 * - Create new company with sector/activity (required)
 * - Edit existing company
 * - Delete company with enhanced confirmation
 * 
 * ============================================================================
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Building2, AlertTriangle } from "lucide-react";

interface CompanyFormData {
  name: string;
  sector: string;
  activity: string;
  company_size?: string;
  country: string;
  zone: string;
  max_open_periods: number;
}

export default function AdminCompanies() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>("");
  const [deleteConfirmation, setDeleteConfirmation] = useState<string>("");
  const [formData, setFormData] = useState<CompanyFormData>({
    name: "",
    sector: "",
    activity: "",
    company_size: "",
    country: "",
    zone: "",
    max_open_periods: 3,
  });

  // Fetch companies
  const { data: companies, isLoading, refetch } = trpc.admin.companies.useQuery();

  // Fetch sectors
  const { data: sectors } = trpc.jobs.sectors.useQuery();

  // Fetch activities based on selected sector
  const { data: activities } = trpc.jobs.activities.useQuery(
    { sector: formData.sector },
    { enabled: !!formData.sector }
  );

  // Mutations
  const createMutation = trpc.admin.createCompany.useMutation({
    onSuccess: () => {
      toast.success("Company created successfully");
      setIsCreateDialogOpen(false);
      refetch();
      resetForm();
    },
    onError: (error) => {
      toast.error(`Failed to create company: ${error.message}`);
    },
  });

  const updateMutation = trpc.admin.updateCompany.useMutation({
    onSuccess: () => {
      toast.success("Company updated successfully");
      setIsEditDialogOpen(false);
      refetch();
      resetForm();
    },
    onError: (error) => {
      toast.error(`Failed to update company: ${error.message}`);
    },
  });

  const deleteMutation = trpc.admin.deleteCompany.useMutation({
    onSuccess: () => {
      toast.success("Company deleted successfully");
      setIsDeleteDialogOpen(false);
      setDeleteConfirmation("");
      refetch();
    },
    onError: (error) => {
      toast.error(`Failed to delete company: ${error.message}`);
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      sector: "",
      activity: "",
      country: "",
      max_open_periods: 3,
    });
    setSelectedCompanyId(null);
    setSelectedCompanyName("");
  };

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!formData.sector) {
      toast.error("Sector is required");
      return;
    }
    if (!formData.activity) {
      toast.error("Activity is required");
      return;
    }
    if (!formData.company_size) {
      toast.error("Company size is required");
      return;
    }

    createMutation.mutate(formData);
  };

  const handleEdit = (company: any) => {
    setSelectedCompanyId(company.id);
    setFormData({
      name: company.name,
      sector: company.sector || "",
      activity: company.activity || "",
      company_size: company.company_size || "",
      country: company.country || "",
      zone: company.zone || "",
      max_open_periods: company.max_open_periods || 3,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedCompanyId) return;

    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!formData.sector) {
      toast.error("Sector is required");
      return;
    }
    if (!formData.activity) {
      toast.error("Activity is required");
      return;
    }

    updateMutation.mutate({
      companyId: selectedCompanyId,
      ...formData,
    });
  };

  const handleDelete = (company: any) => {
    setSelectedCompanyId(company.id);
    setSelectedCompanyName(company.name);
    setDeleteConfirmation("");
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedCompanyId) return;

    // Vérifier que l'utilisateur a tapé le nom exact de l'entreprise
    if (deleteConfirmation !== selectedCompanyName) {
      toast.error(`Please type "${selectedCompanyName}" to confirm deletion`);
      return;
    }

    deleteMutation.mutate({ companyId: selectedCompanyId });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">Companies Management</h1>
              <p className="text-muted-foreground">
                Manage companies and their sectors/activities
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Company
          </Button>
        </div>

        {/* Companies Table */}
        <Card>
          <CardHeader>
            <CardTitle>Companies ({companies?.length || 0})</CardTitle>
            <CardDescription>
              All companies in the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {companies && companies.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Activity</TableHead>
                    <TableHead>Max Open Periods</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((company: any) => (
                    <TableRow key={company.id}>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>{company.sector || "-"}</TableCell>
                      <TableCell>{company.activity || "-"}</TableCell>
                      <TableCell>{company.max_open_periods || 3}</TableCell>
                      <TableCell>
                        {new Date(company.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(company)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(company)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No companies found. Create your first company!
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Company</DialogTitle>
              <DialogDescription>
                Add a new company to the system
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Acme Corp"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sector">Sector *</Label>
                <Select 
                  value={formData.sector} 
                  onValueChange={(value) => setFormData({ ...formData, sector: value, activity: "" })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors && sectors.length > 0 ? (
                      sectors.map((s: string) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-sectors" disabled>
                        No sectors available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {formData.sector && (
                <div className="space-y-2">
                  <Label htmlFor="activity">Activity *</Label>
                  <Select value={formData.activity} onValueChange={(value) => setFormData({ ...formData, activity: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select activity" />
                    </SelectTrigger>
                    <SelectContent>
                      {activities && activities.length > 0 ? (
                        activities.map((a: any) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.label}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-activities" disabled>
                          No activities available for this sector
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="company_size">Company Size *</Label>
                <Select value={formData.company_size || ""} onValueChange={(value) => setFormData({ ...formData, company_size: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TPE">TPE (&lt; 10 employees)</SelectItem>
                    <SelectItem value="PME">PME (10-250 employees)</SelectItem>
                    <SelectItem value="ETI">ETI (250-5000 employees)</SelectItem>
                    <SelectItem value="GE">GE (&gt; 5000 employees)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="e.g., France, Morocco, etc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="zone">Zone/Quartier</Label>
                <Input
                  id="zone"
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                  placeholder="e.g., Maarif, Ain Diab, etc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_open_periods">Max Open Periods</Label>
                <Input
                  id="max_open_periods"
                  type="number"
                  min="1"
                  value={formData.max_open_periods}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_open_periods: parseInt(e.target.value) || 3,
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Company</DialogTitle>
              <DialogDescription>
                Update company information
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Company Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Acme Corp"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-sector">Sector *</Label>
                <Select 
                  value={formData.sector} 
                  onValueChange={(value) => setFormData({ ...formData, sector: value, activity: "" })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors && sectors.length > 0 ? (
                      sectors.map((s: string) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-sectors" disabled>
                        No sectors available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {formData.sector && (
                <div className="space-y-2">
                  <Label htmlFor="edit-activity">Activity *</Label>
                  <Select value={formData.activity} onValueChange={(value) => setFormData({ ...formData, activity: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select activity" />
                    </SelectTrigger>
                    <SelectContent>
                      {activities && activities.length > 0 ? (
                        activities.map((a: any) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.label}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-activities" disabled>
                          No activities available for this sector
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit-company_size">Company Size *</Label>
                <Select value={formData.company_size || ""} onValueChange={(value) => setFormData({ ...formData, company_size: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TPE">TPE (&lt; 10 employees)</SelectItem>
                    <SelectItem value="PME">PME (10-250 employees)</SelectItem>
                    <SelectItem value="ETI">ETI (250-5000 employees)</SelectItem>
                    <SelectItem value="GE">GE (&gt; 5000 employees)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-country">Country</Label>
                <Input
                  id="edit-country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="e.g., France, Morocco, etc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-zone">Zone/Quartier</Label>
                <Input
                  id="edit-zone"
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                  placeholder="e.g., Maarif, Ain Diab, etc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-max_open_periods">Max Open Periods</Label>
                <Input
                  id="edit-max_open_periods"
                  type="number"
                  min="1"
                  value={formData.max_open_periods}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_open_periods: parseInt(e.target.value) || 3,
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog - Enhanced */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) {
            setDeleteConfirmation("");
          }
        }}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Delete Company
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Warning Alert */}
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Warning</AlertTitle>
                <AlertDescription>
                  Deleting this company will permanently remove:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>All associated users</li>
                    <li>All periods and emission entries</li>
                    <li>All related data and history</li>
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Company Name Display */}
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">
                  Company to delete:
                </p>
                <p className="text-lg font-semibold">{selectedCompanyName}</p>
              </div>

              {/* Confirmation Input */}
              <div className="space-y-2">
                <Label htmlFor="delete-confirm">
                  Type <span className="font-mono font-bold">{selectedCompanyName}</span> to confirm:
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder={`Type "${selectedCompanyName}" here`}
                  className={
                    deleteConfirmation && deleteConfirmation !== selectedCompanyName
                      ? "border-destructive"
                      : ""
                  }
                />
                {deleteConfirmation && deleteConfirmation !== selectedCompanyName && (
                  <p className="text-sm text-destructive">
                    Company name doesn't match
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeleteConfirmation("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={
                  deleteMutation.isPending || 
                  deleteConfirmation !== selectedCompanyName
                }
              >
                {deleteMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

